import React, { useState, useMemo } from 'react';

const EcommerceSidebar = ({
  rubros,
  selectedRubro,
  onSelectRubro,
  familias = [],
  selectedFamilia = null,
  onSelectFamilia,
  rubroCounts = {},
  brands = [],
  selectedBrands = [],
  onToggleBrand,
  sortBy,
  setSortBy,
  onlyInStock,
  setOnlyInStock,
  onlyOffers = false,
  setOnlyOffers,
  presentationFilter = 'all',
  setPresentationFilter,
  availableColors = [],
  selectedColors = [],
  onToggleColor,
  availableSizes = [],
  selectedSizes = [],
  onToggleSize,
  totalProductsCount,
  onResetFilters,
  onCollapse,
  filterModules = {}
}) => {
  const [categorySearch, setCategorySearch] = useState('');
  const [familiaSearch, setFamiliaSearch] = useState('');

  // Estado para colapsar/expandir individualmente cada sección de filtros
  const [collapsedSections, setCollapsedSections] = useState({
    promociones: false,
    presentacion: false,
    colores: false,
    talles: false,
    categorias: false,
    familias: false,
    marcas: false,
    disponibilidad: false,
    ordenar: false
  });

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Filtrar la lista de rubros en el buscador de categorías ordenados alfabéticamente
  const filteredRubros = useMemo(() => {
    const sorted = [...(rubros || [])].sort((a, b) => {
      const nameA = (a.d_rubro || `Rubro ${a.c_rubro || ''}`).trim().toUpperCase();
      const nameB = (b.d_rubro || `Rubro ${b.c_rubro || ''}`).trim().toUpperCase();
      return nameA.localeCompare(nameB, 'es-AR');
    });
    if (!categorySearch.trim()) return sorted;
    const term = categorySearch.toLowerCase().trim();
    return sorted.filter(r => (r.d_rubro || '').toLowerCase().includes(term) || String(r.c_rubro).includes(term));
  }, [rubros, categorySearch]);

  // Filtrar la lista de familias/subrubros en su buscador
  const filteredFamilias = useMemo(() => {
    const list = Array.isArray(familias) ? familias : [];
    if (!familiaSearch.trim()) return list;
    const term = familiaSearch.toLowerCase().trim();
    return list.filter(f => (f.d_famil || '').toLowerCase().includes(term));
  }, [familias, familiaSearch]);

  const hasActiveFilters = Boolean(
    selectedRubro !== null ||
    selectedFamilia !== null ||
    (filterModules?.filterBrands !== false && selectedBrands && selectedBrands.length > 0) ||
    (filterModules?.filterStock !== false && onlyInStock) ||
    (filterModules?.filterOffers !== false && onlyOffers) ||
    (filterModules?.filterSort !== false && sortBy !== 'default') ||
    (filterModules?.filterPresentation !== false && presentationFilter !== 'all') ||
    (filterModules?.filterColors !== false && selectedColors && selectedColors.length > 0) ||
    (filterModules?.filterSizes !== false && selectedSizes && selectedSizes.length > 0)
  );

  return (
    <div className="card border-0 shadow-sm rounded-4 p-3 mb-4 ecom-sidebar-card">
      {/* Título, Limpiar Filtros y Botón Discreto para Ocultar Barra Lateral */}
      <div className="d-flex align-items-center justify-content-between pb-2 border-bottom mb-3">
        <div className="d-flex align-items-center gap-2">
          <h6 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
            <i className="bi bi-funnel-fill text-primary"></i> Filtros
          </h6>
          {hasActiveFilters && (
            <button
              type="button"
              className="btn btn-sm btn-link text-danger text-decoration-none p-0 small fw-semibold ms-1"
              onClick={onResetFilters}
              title="Limpiar todos los filtros"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Botón discreto "<" para colapsar la barra en desktop */}
        {onCollapse && (
          <button
            type="button"
            className="ecom-collapse-trigger-btn d-none d-lg-inline-flex"
            onClick={onCollapse}
            title="Ocultar filtros (<)"
          >
            <i className="bi bi-chevron-left" style={{ fontSize: '0.75rem' }}></i>
          </button>
        )}
      </div>

      {/* 1. Rubros / Categorías */}
      <div className="border-bottom pb-3 mb-3">
        <div 
          className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
          onClick={() => toggleSection('categorias')}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-center gap-1">
            <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
              Categorías
            </label>
            {selectedRubro !== null && (
              <span className="badge bg-primary-subtle text-primary ms-1 font-monospace">1</span>
            )}
          </div>
          <i className={`bi bi-chevron-${collapsedSections.categorias ? 'down' : 'up'} text-muted small`}></i>
        </div>

        {!collapsedSections.categorias && (
          <div className="mt-2">
            {/* Buscador rápido de categorías si hay muchas */}
            {rubros.length > 8 && (
              <div className="position-relative mb-2">
                <input
                  type="text"
                  className="form-control form-control-sm rounded-3 py-1 pe-4"
                  style={{ fontSize: '0.8rem', backgroundColor: '#f8fafc' }}
                  placeholder="Filtrar categorías..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
                {categorySearch && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
                    onClick={() => setCategorySearch('')}
                  >
                    <i className="bi bi-x small"></i>
                  </button>
                )}
              </div>
            )}

            {/* Lista de Categorías */}
            <div className="ecom-filter-list">
              <button
                type="button"
                className={`ecom-filter-item ${selectedRubro === null ? 'active' : ''}`}
                onClick={() => onSelectRubro(null)}
              >
                <span className="item-text">Todos los productos</span>
                <span className="badge bg-light text-dark rounded-pill border small">{totalProductsCount}</span>
              </button>
              {filteredRubros.map((r) => {
                const rubroKey = String(r.c_rubro || r.id).trim();
                const count = rubroCounts[rubroKey];
                const isSelected = selectedRubro !== null && String(selectedRubro).trim() === rubroKey;

                return (
                  <button
                    key={rubroKey}
                    type="button"
                    className={`ecom-filter-item ${isSelected ? 'active' : ''}`}
                    onClick={() => onSelectRubro(isSelected ? null : r.c_rubro)}
                    title={r.d_rubro}
                  >
                    <span className="item-text">{r.d_rubro || `Rubro ${r.c_rubro}`}</span>
                    <span className={`badge rounded-pill small ${isSelected ? 'bg-primary text-white' : 'bg-light text-muted border'}`}>
                      {count !== undefined ? count : 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 2. Subrubros / Familias (Aparece como sección dedicada cuando hay un rubro seleccionado y existen familias) */}
      {selectedRubro !== null && Array.isArray(familias) && familias.length > 0 && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('familias')}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
                Subrubros / Familias
              </label>
              {selectedFamilia !== null && (
                <span className="badge bg-primary-subtle text-primary ms-1 font-monospace">1</span>
              )}
            </div>
            <i className={`bi bi-chevron-${collapsedSections.familias ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.familias && (
            <div className="mt-2">
              {familias.length > 8 && (
                <div className="position-relative mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm rounded-3 py-1 pe-4"
                    style={{ fontSize: '0.8rem', backgroundColor: '#f8fafc' }}
                    placeholder="Filtrar subrubros..."
                    value={familiaSearch}
                    onChange={(e) => setFamiliaSearch(e.target.value)}
                  />
                  {familiaSearch && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-muted position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
                      onClick={() => setFamiliaSearch('')}
                    >
                      <i className="bi bi-x small"></i>
                    </button>
                  )}
                </div>
              )}

              <div className="ecom-filter-list" style={{ maxHeight: '220px' }}>
                <button
                  type="button"
                  className={`ecom-filter-item ${selectedFamilia === null ? 'active' : ''}`}
                  onClick={() => onSelectFamilia && onSelectFamilia(null)}
                >
                  <span className="item-text">Todos los subrubros</span>
                  {selectedFamilia === null && <i className="bi bi-check-lg text-primary ms-auto"></i>}
                </button>
                {filteredFamilias.map((fam) => {
                  const famKey = String(fam.c_famil || fam.id).trim();
                  const isFamSelected = selectedFamilia !== null && String(selectedFamilia).trim() === famKey;

                  return (
                    <button
                      key={famKey}
                      type="button"
                      className={`ecom-filter-item ${isFamSelected ? 'active' : ''}`}
                      onClick={() => onSelectFamilia && onSelectFamilia(isFamSelected ? null : famKey)}
                      title={fam.d_famil}
                    >
                      <span className="item-text">{fam.d_famil}</span>
                      {isFamSelected && <i className="bi bi-check-lg text-primary ms-auto"></i>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 2. Ordenar por */}
      {filterModules?.filterSort !== false && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('ordenar')}
            style={{ cursor: 'pointer' }}
          >
            <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
              Ordenar por
            </label>
            <i className={`bi bi-chevron-${collapsedSections.ordenar ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.ordenar && (
            <div className="mt-2">
              <select
                className="form-select form-select-sm rounded-3"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">Relevancia / Código</option>
                <option value="name_asc">Nombre (A - Z)</option>
                <option value="name_desc">Nombre (Z - A)</option>
                <option value="price_asc">Menor Precio</option>
                <option value="price_desc">Mayor Precio</option>
              </select>
            </div>
          )}
        </div>
      )}

      {/* 3. Disponibilidad */}
      {filterModules?.filterStock !== false && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('disponibilidad')}
            style={{ cursor: 'pointer' }}
          >
            <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
              Disponibilidad
            </label>
            <i className={`bi bi-chevron-${collapsedSections.disponibilidad ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.disponibilidad && (
            <div className="mt-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="inStockCheck"
                  checked={onlyInStock}
                  onChange={(e) => setOnlyInStock(e.target.checked)}
                />
                <label className="form-check-label small text-dark" htmlFor="inStockCheck">
                  Solo artículos con stock
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 4. Promociones */}
      {filterModules?.filterOffers !== false && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('promociones')}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
                Promociones
              </label>
              {onlyOffers && (
                <span className="badge bg-danger-subtle text-danger ms-1 font-monospace">1</span>
              )}
            </div>
            <i className={`bi bi-chevron-${collapsedSections.promociones ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.promociones && (
            <div className="mt-2">
              <div className="form-check d-flex align-items-center">
                <input
                  className="form-check-input mt-0 me-2"
                  type="checkbox"
                  id="onlyOffersCheck"
                  checked={onlyOffers}
                  onChange={(e) => setOnlyOffers && setOnlyOffers(e.target.checked)}
                />
                <label className="form-check-label small text-dark d-flex align-items-center cursor-pointer" htmlFor="onlyOffersCheck">
                  <span className="badge bg-danger text-white px-2 py-0.5 rounded-1 me-2 shadow-xs" style={{ fontSize: '0.68rem', fontWeight: 600 }}>
                    <i className="bi bi-tag-fill me-1"></i>OFERTA
                  </span>
                  <span>Solo productos en oferta</span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5. Talle / Medida */}
      {filterModules?.filterSizes !== false && availableSizes && availableSizes.length > 0 && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('talles')}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
                Talle / Medida
              </label>
              {selectedSizes.length > 0 && (
                <span className="badge bg-primary-subtle text-primary ms-1 font-monospace">
                  {selectedSizes.length}
                </span>
              )}
            </div>
            <i className={`bi bi-chevron-${collapsedSections.talles ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.talles && (
            <div className="mt-2">
              <div className="d-flex flex-wrap gap-1 mb-2">
                {availableSizes.map((size) => {
                  const isSelected = selectedSizes.includes(size);
                  return (
                    <button
                      key={size}
                      type="button"
                      className={`btn btn-sm rounded-3 px-2.5 py-1 fw-semibold ${
                        isSelected 
                          ? 'btn-primary shadow-sm' 
                          : 'btn-outline-secondary border-light-subtle bg-light text-dark'
                      }`}
                      style={{ minWidth: '36px', fontSize: '0.8rem' }}
                      onClick={() => onToggleSize(size)}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>

              {selectedSizes.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top border-light-subtle">
                  <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                    {selectedSizes.length} {selectedSizes.length === 1 ? 'talle' : 'talles'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted p-0 text-decoration-none small"
                    onClick={() => onToggleSize(null)}
                  >
                    Quitar talles
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 6. Color / Variedad */}
      {filterModules?.filterColors !== false && availableColors && availableColors.length > 0 && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('colores')}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
                Color / Variedad
              </label>
              {selectedColors.length > 0 && (
                <span className="badge bg-danger-subtle text-danger ms-1 font-monospace">
                  {selectedColors.length}
                </span>
              )}
            </div>
            <i className={`bi bi-chevron-${collapsedSections.colores ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.colores && (
            <div className="mt-2">
              <div className="ecom-color-swatches mb-2">
                {availableColors.map((color) => {
                  const isSelected = selectedColors.includes(color.id);
                  let customClass = 'ecom-color-swatch-item';
                  if (color.isRainbow) customClass += ' ecom-color-swatch-rainbow';
                  if (color.isTransparent) customClass += ' ecom-color-swatch-trans';
                  if (isSelected) customClass += ' selected';

                  return (
                    <div
                      key={color.id}
                      className={customClass}
                      style={{
                        backgroundColor: color.hex || '#ffffff',
                        border: color.border ? '1px solid #94a3b8' : '2px solid #ffffff'
                      }}
                      title={color.name}
                      onClick={() => onToggleColor(color.id)}
                    >
                      {isSelected && (
                        <i
                          className="bi bi-check-lg fw-bold"
                          style={{
                            fontSize: '0.85rem',
                            color: color.hex === '#ffffff' || color.id === 'amarillo' || color.isTransparent ? '#0f172a' : '#ffffff'
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedColors.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top border-light-subtle">
                  <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                    {selectedColors.length} {selectedColors.length === 1 ? 'color' : 'colores'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted p-0 text-decoration-none small"
                    onClick={() => onToggleColor(null)}
                  >
                    Quitar colores
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 7. Marcas (Solo visible si hay marcas reales en los artículos y el módulo está activo) */}
      {filterModules?.filterBrands !== false && brands && brands.length > 0 && (
        <div className="border-bottom pb-3 mb-3">
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('marcas')}
            style={{ cursor: 'pointer' }}
          >
            <div className="d-flex align-items-center gap-1">
              <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
                Marcas
              </label>
              {selectedBrands.length > 0 && (
                <span className="badge bg-primary-subtle text-primary ms-1 font-monospace">
                  {selectedBrands.length}
                </span>
              )}
            </div>
            <i className={`bi bi-chevron-${collapsedSections.marcas ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.marcas && (
            <div className="mt-2">
              <div className="ecom-filter-list" style={{ maxHeight: '180px' }}>
                <button
                  type="button"
                  className={`ecom-filter-item ${selectedBrands.length === 0 ? 'active' : ''}`}
                  onClick={() => onToggleBrand(null)}
                >
                  <span className="item-text">Todas las marcas</span>
                </button>
                {brands.map((b) => {
                  const brandKey = typeof b === 'object' ? b.id : b;
                  const brandLabel = typeof b === 'object' ? b.label : b;
                  const isSelected = selectedBrands.includes(brandKey);
                  return (
                    <button
                      key={brandKey}
                      type="button"
                      className={`ecom-filter-item ${isSelected ? 'active' : ''}`}
                      onClick={() => onToggleBrand(brandKey)}
                      title={brandLabel}
                    >
                      <span className="d-flex align-items-center gap-2 item-text">
                        <input 
                          type="checkbox" 
                          className="form-check-input m-0 cursor-pointer" 
                          checked={isSelected} 
                          onChange={() => {}} 
                          style={{ pointerEvents: 'none' }}
                        />
                        <span>{brandLabel}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {selectedBrands.length > 0 && (
                <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top border-light-subtle">
                  <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                    {selectedBrands.length} {selectedBrands.length === 1 ? 'marca' : 'marcas'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-muted p-0 text-decoration-none small"
                    onClick={() => onToggleBrand(null)}
                  >
                    Quitar marcas
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 8. Filtro de Presentación: Unidad / Bulto / Todos */}
      {filterModules?.filterPresentation !== false && (
        <div>
          <div 
            className="d-flex align-items-center justify-content-between py-1 cursor-pointer"
            onClick={() => toggleSection('presentacion')}
            style={{ cursor: 'pointer' }}
          >
            <label className="form-label small fw-bold text-muted text-uppercase mb-0 cursor-pointer" style={{ letterSpacing: '0.5px', cursor: 'pointer' }}>
              Presentación {presentationFilter !== 'all' && <span className="badge bg-primary-subtle text-primary ms-1 font-monospace">1</span>}
            </label>
            <i className={`bi bi-chevron-${collapsedSections.presentacion ? 'down' : 'up'} text-muted small`}></i>
          </div>

          {!collapsedSections.presentacion && (
            <div className="ecom-presentation-group mt-2">
              <button
                type="button"
                className={`ecom-presentation-btn ${presentationFilter === 'all' ? 'active' : ''}`}
                onClick={() => setPresentationFilter('all')}
              >
                <span>Todos</span>
              </button>
              <button
                type="button"
                className={`ecom-presentation-btn ${presentationFilter === 'unit' ? 'active' : ''}`}
                onClick={() => setPresentationFilter('unit')}
                title="Artículos por unidad suelta"
              >
                <i className="bi bi-box"></i>
                <span>Unidad</span>
              </button>
              <button
                type="button"
                className={`ecom-presentation-btn ${presentationFilter === 'bulk' ? 'active' : ''}`}
                onClick={() => setPresentationFilter('bulk')}
                title="Artículos en bultos cerrados / packs mayoristas"
              >
                <i className="bi bi-boxes"></i>
                <span>Bultos</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EcommerceSidebar;

